import React, { useEffect, useState } from 'react';
import { getAllTemplates, createTemplate, updateTemplate, toggleTemplate, deleteTemplate } from '../services/messageTemplates';

const MessageTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    subject: '',
    body: '',
    template_type: 'broadcast',
    variables: []
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await getAllTemplates();
      setTemplates(response.templates || response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      alert('Failed to load templates: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const variables = formData.variables.length > 0 
        ? formData.variables 
        : extractVariables(formData.subject + ' ' + formData.body);
      
      const templateData = { ...formData, variables };

      if (editingTemplate) {
        await updateTemplate(editingTemplate.id, templateData);
        alert('Template updated successfully!');
      } else {
        await createTemplate(templateData);
        alert('Template created successfully!');
      }
      
      setShowModal(false);
      setEditingTemplate(null);
      setFormData({ name: '', subject: '', body: '', template_type: 'broadcast', variables: [] });
      fetchTemplates();
    } catch (error) {
      alert('Failed to save template: ' + error.message);
    }
  };

  const extractVariables = (text) => {
    const matches = text.match(/{{(\w+)}}/g);
    return matches ? [...new Set(matches.map(m => m.replace(/{{|}}/g, '')))] : [];
  };

  const handleToggle = async (id) => {
    try {
      await toggleTemplate(id);
      fetchTemplates();
    } catch (error) {
      alert('Failed to toggle template: ' + error.message);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await deleteTemplate(id);
        fetchTemplates();
      } catch (error) {
        alert('Failed to delete template: ' + error.message);
      }
    }
  };

  const openEditModal = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      subject: template.subject,
      body: template.body,
      template_type: template.template_type,
      variables: template.variables || []
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingTemplate(null);
    setFormData({ name: '', subject: '', body: '', template_type: 'broadcast', variables: [] });
    setShowModal(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">
      <div className="text-xl">Loading templates...</div>
    </div>;
  }

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-bold">Message Templates</h1>
        <button
          onClick={openCreateModal}
          className="bg-blue-600 text-white px-4 md:px-6 py-2 rounded-lg hover:bg-blue-700 text-sm md:text-base w-full sm:w-auto"
        >
          + Create Template
        </button>
      </div>

      <div className="grid gap-4">
        {templates.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No templates found. Create your first template!</p>
          </div>
        ) : (
          templates.map((template) => (
            <div key={template.id} className="bg-white border rounded-lg p-4 shadow-sm">
              <div className="flex flex-col md:flex-row justify-between items-start gap-4">
                <div className="flex-1">
                  <h3 className="text-lg md:text-xl font-semibold">{template.name}</h3>
                  <p className="text-sm md:text-base text-gray-600 mt-1"><strong>Type:</strong> {template.template_type}</p>
                  <p className="text-sm md:text-base text-gray-600"><strong>Subject:</strong> {template.subject}</p>
                  <div className="mt-2">
                    <p className="text-xs md:text-sm text-gray-500">Variables: {template.variables?.join(', ') || 'None'}</p>
                  </div>
                  <div className="mt-2">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs md:text-sm ${template.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                      {template.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                  <button
                    onClick={() => handleToggle(template.id)}
                    className={`px-3 md:px-4 py-2 rounded text-sm ${template.is_active ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'} hover:opacity-80`}
                  >
                    {template.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => openEditModal(template)}
                    className="px-3 md:px-4 py-2 bg-blue-100 text-blue-800 rounded hover:bg-blue-200 text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className="px-3 md:px-4 py-2 bg-red-100 text-red-800 rounded hover:bg-red-200 text-sm"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-4 md:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl md:text-2xl font-bold mb-4">
              {editingTemplate ? 'Edit Template' : 'Create Template'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Template Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Template Type</label>
                <select
                  value={formData.template_type}
                  onChange={(e) => setFormData({ ...formData, template_type: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="broadcast">Broadcast</option>
                  <option value="reminder">Reminder</option>
                  <option value="welcome">Welcome</option>
                  <option value="notification">Notification</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Subject</label>
                <input
                  type="text"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Use {{firstName}}, {{email}}, etc."
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">Body (HTML)</label>
                <textarea
                  value={formData.body}
                  onChange={(e) => setFormData({ ...formData, body: e.target.value })}
                  className="w-full border rounded px-3 py-2 h-40"
                  placeholder="<h2>Hi {{firstName}},</h2><p>Your message here...</p>"
                  required
                />
              </div>

              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Available variables:</strong> {'{{'}firstName{'}}'}, {'{{'}lastName{'}}'}, {'{{'}email{'}}'}, {'{{'}daysSinceRegistration{'}}'}, {'{{'}verificationLink{'}}'}, {'{{'}subscriptionLink{'}}'}
                </p>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingTemplate(null);
                  }}
                  className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingTemplate ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageTemplates;
